import React, { useState, useEffect, useRef } from 'react';
import '../../styles/CodeInput.css';

/**
 * CodeInput: Renders N masked input boxes for entering a code.
 * 
 * Props:
 * - length: Number of digits
 * - value: current codeValue (string)
 * - onChange: (newCodeValue) => void
 * - autoFocus: boolean, focus first box on mount
 */
const CodeInput = ({ length = 6, value = '', onChange, autoFocus = false }) => {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputsRef = useRef([]);

  // Initialize digits from value
  useEffect(() => {
    const vals = value.split('').slice(0, length);
    const arr = [...vals, ...Array(length - vals.length).fill('')];
    setDigits(arr);
  }, [value, length]);

  // Focus first
  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (idx, e) => {
    let char = e.target.value;
    if (!char) {
      updateDigits(idx, '');
      return;
    }
    // allow only one character, numeric
    char = char.slice(-1).replace(/\D/, '');
    if (!char) return;
    updateDigits(idx, char);
    // move focus
    if (inputsRef.current[idx + 1]) {
      inputsRef.current[idx + 1].focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1].focus();
    }
  };

  const updateDigits = (idx, char) => {
    const newDigits = [...digits];
    newDigits[idx] = char;
    setDigits(newDigits);
    onChange(newDigits.join(''));
  };

  const clearCode = () => {
    const newDigits = Array(length).fill('');
    setDigits(newDigits);
    onChange('');
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  };

  return (
    <div className="code-input-wrapper">
      <div className="code-input-container">
        {digits.map((d, i) => (
          <input
            key={i}
            type="password"
            className="code-input-box"
            maxLength={1}
            value={d}
            onChange={e => handleChange(i, e)}
            onKeyDown={e => handleKeyDown(i, e)}
            ref={el => (inputsRef.current[i] = el)}
            inputMode="numeric"
            autoComplete="one-time-code"
          />
        ))}
      </div>
      <button 
        className="code-input-clear"
        onClick={clearCode}
        type="button"
        aria-label="Clear code"
      >
        ✕
      </button>
    </div>
  );
};

export default CodeInput;